import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { DialogService } from 'app/backpack/dialog-modal/dialog.service';
import { SchoolActivity, SchoolActivityInstance, SchoolActivityState } from 'app/models';
import { HttpService } from 'app/services/http-service';
import {
	CreateSchoolActivityReq,
	GetActivityInstancesReq,
	InstanceTimes,
	SchoolActivityService,
	ToBeDeletedCountReq,
	ToWireInstanceTimes,
	UpdateSchoolActivityRequest,
	WireInstanceTimes,
} from 'app/services/school-activity.service';
import { isEqual } from 'lodash';
import * as moment from 'moment';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { TimeRangeInputType } from '../../shared/time-range-input/time-range-input/time-range-input.component';
import {
	activityScheduleFormFields,
	CUSTOM_SCHEDULE_DATE_KEY,
	CUSTOM_SCHEDULE_REPEAT_KEY,
	CUSTOM_SCHEDULE_TIME_RANGE_KEY,
	DEFAULT_REPEAT_END_DATE,
	FLEX_INSTANCE_TIMES_KEY,
	FLEX_PERIOD_ID_KEY,
	FLEX_PERIOD_REPEATS_KEY,
	getActivityStartDate,
	getActivityTimeRange,
	getCustomStartEndTime,
	SCHEDULE_REPEAT_END_DATE_KEY,
	SCHEDULE_TYPE_KEY,
} from '../activity-form-util';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: '[sp-edit-activity-schedule-form]',
	host: {
		'[class.!tw-cursor-progress]': 'activityInstancesLoading$.value',
		'(click)': 'openEditScheduleModal()',
	},
	template: `
		<ng-content></ng-content>

		<ng-template #editScheduleModalBody let-dismiss>
			<bp-dialog-header>Edit Schedule</bp-dialog-header>
			<bp-dialog-body>
				<sp-activity-schedule-form
					[editScheduleForm]="editScheduleForm"
					[activity]="activity"
					[activityInstances]="activityInstances$"></sp-activity-schedule-form>
			</bp-dialog-body>
			<bp-dialog-footer>
				<bp-button (click)="dismiss()" variant="white" title="Cancel"></bp-button>
				<bp-button (click)="saveScheduleChanges()" variant="color" title="Save" [disabled]="getSaveButtonState()"></bp-button>
			</bp-dialog-footer>
		</ng-template>

		<!--  WARNING MODAL -->
		<ng-template #warningModalBody let-dismissNested>
			<bp-dialog-header>{{ getWarningModalHeader() }}</bp-dialog-header>
			<bp-dialog-body>
				<ng-container *ngIf="sessionRemovalCount === activityInstances$.value.length">
					<div>
						Changing the activity’s schedule will delete {{ sessionRemovalCount }} upcoming ‘{{ activity.name }}’ session and create new ones
						according to the updated schedule. Students will need to be added again, but past sessions won’t be affected.
					</div>
					<div class="tw-mt-3">Visibility settings might also be impacted. This action can’t be undone.</div>
				</ng-container>
				<ng-container *ngIf="sessionRemovalCount < activityInstances$.value.length">
					<div>
						Changing the activity’s schedule will delete {{ sessionRemovalCount }} upcoming ‘{{ activity.name }}’ session. Past sessions won’t be
						affected. This action can’t be undone.
					</div>
				</ng-container>
			</bp-dialog-body>
			<bp-dialog-footer>
				<bp-button (click)="dismissWarningModal()" variant="white" title="Cancel"></bp-button>
				<bp-button (click)="confirmScheduleChanges()" variant="danger" [title]="getSessionRemovalButtonText()"></bp-button>
			</bp-dialog-footer>
		</ng-template>
		<!-- END  WARNING MODAL -->
	`,
})
export class EditActivityScheduleFormComponent {
	@Input() activity!: SchoolActivity;
	@Input() canEdit = false;

	// Schedule Form
	@ViewChild('editScheduleModalBody', {
		read: TemplateRef,
		static: false,
	})
	editScheduleModalBody!: TemplateRef<unknown>;
	editScheduleDialogRef: MatDialogRef<unknown> | null = null;
	destroyDialogRef: MatDialogRef<unknown> | null = null;
	sessionRemovalCount = 0;
	sessionRemovalButton = '';
	editScheduleForm = new FormGroup(activityScheduleFormFields);
	activityInstances$ = new BehaviorSubject<SchoolActivityInstance[]>([]);
	activityInstancesLoading$ = new BehaviorSubject<boolean>(false);

	// Destructive change warning
	@ViewChild('warningModalBody', {
		read: TemplateRef,
		static: false,
	})
	warningModalBody!: TemplateRef<unknown>;

	constructor(
		private backpackDialogService: DialogService,
		private httpService: HttpService,
		private schoolActivityService: SchoolActivityService,
		private cdr: ChangeDetectorRef
	) {}

	getWarningModalHeader(): string {
		const plural = this.sessionRemovalCount > 1 ? 'sessions' : 'session';
		return `Delete  ${this.sessionRemovalCount} upcoming ‘${this.activity.name}’ ${plural}?`;
	}

	getSessionRemovalButtonText(): string {
		return `Delete ${this.sessionRemovalCount} Sessions`;
	}

	protected updateActivity(updateReq: Partial<UpdateSchoolActivityRequest>): void {
		this.schoolActivityService.UpdateActivity(updateReq, this.activity);
	}

	async openEditScheduleModal(): Promise<void> {
		if (!this.canEdit || this.activityInstancesLoading$.value) {
			return;
		}

		this.activityInstancesLoading$.next(true);

		// Reset form data when opening modal
		const tz = this.httpService.currentSchoolSubject.getValue()!.timezone;
		this.editScheduleForm.setValue({
			[FLEX_PERIOD_ID_KEY]: this.activity.flex_period_id,
			[FLEX_PERIOD_REPEATS_KEY]: this.activity.state,
			[FLEX_INSTANCE_TIMES_KEY]: [],
			[SCHEDULE_REPEAT_END_DATE_KEY]: this.activity.repeat_end_date ? moment(this.activity.repeat_end_date) : DEFAULT_REPEAT_END_DATE,
			[SCHEDULE_TYPE_KEY]: this.activity.schedule_type,
			[CUSTOM_SCHEDULE_DATE_KEY]: getActivityStartDate(this.activity, tz),
			[CUSTOM_SCHEDULE_TIME_RANGE_KEY]: getActivityTimeRange(this.activity, tz),
			[CUSTOM_SCHEDULE_REPEAT_KEY]: this.activity.custom_repeat_rule ?? '',
		});

		const startDate = new Date();
		const endDate = this.activity.repeat_end_date ? moment(this.activity.repeat_end_date).toDate() : DEFAULT_REPEAT_END_DATE.toDate();

		const req: GetActivityInstancesReq = {
			start_time: startDate,
			end_time: endDate,
			activity_id: this.activity.id,
		};

		try {
			// Refresh instances when opening schedule modal
			const instances = await firstValueFrom(this.schoolActivityService.GetActivityInstancesHTTP(req));
			this.activityInstances$.next(instances);
			this.editScheduleDialogRef = this.backpackDialogService.openDialog(this.editScheduleModalBody, this.cdr);
		} finally {
			this.activityInstancesLoading$.next(false);
		}
	}

	protected getSaveButtonState(): boolean {
		const formValue = this.editScheduleForm.value;
		const scheduleType = formValue[SCHEDULE_TYPE_KEY];

		let disablePrimaryButton = true;

		// Check for data differences
		const sameEndDate = this.activity.repeat_end_date
			? moment(formValue[SCHEDULE_REPEAT_END_DATE_KEY]).isSame(moment(this.activity.repeat_end_date), 'minute')
			: true;

		if (scheduleType != this.activity.schedule_type) {
			disablePrimaryButton = false;
		} else if (scheduleType === 'flex_period') {
			const sameFlexPeriod = formValue[FLEX_PERIOD_ID_KEY] === this.activity.flex_period_id;
			const sameRepeats = formValue[FLEX_PERIOD_REPEATS_KEY] === this.activity.state;
			const noNewInstances = formValue[FLEX_INSTANCE_TIMES_KEY] ? formValue[FLEX_INSTANCE_TIMES_KEY].length === 0 : true;

			disablePrimaryButton = sameFlexPeriod && sameRepeats && noNewInstances && sameEndDate;
		} else {
			const tz = this.httpService.currentSchoolSubject.getValue()!.timezone;
			const sameDate = getActivityStartDate(this.activity, tz).isSame(formValue[CUSTOM_SCHEDULE_DATE_KEY]);
			const sameTime = isEqual(formValue[CUSTOM_SCHEDULE_TIME_RANGE_KEY], getActivityTimeRange(this.activity, tz));
			const sameRRule = isEqual(formValue[CUSTOM_SCHEDULE_REPEAT_KEY], this.activity.custom_repeat_rule);

			disablePrimaryButton = sameDate && sameTime && sameRRule && sameEndDate;
		}

		// Check for invalid fields
		const validEndDate = this.editScheduleForm.get(SCHEDULE_REPEAT_END_DATE_KEY)?.valid;

		disablePrimaryButton = disablePrimaryButton || !validEndDate;

		return disablePrimaryButton;
	}

	protected async saveScheduleChanges(confirmedDestructiveChanges = false): Promise<void> {
		const scheduleType = this.editScheduleForm.value[SCHEDULE_TYPE_KEY];
		const scheduleTypeChanged = scheduleType !== this.activity.schedule_type;
		const updateReq: Partial<UpdateSchoolActivityRequest> = {
			id: this.activity.id,
			schedule_type: scheduleType,
		};
		if (!confirmedDestructiveChanges && !this.destroyDialogRef) {
			const formValue = this.editScheduleForm.value;
			const activityData: CreateSchoolActivityReq = {
				name: this.activity.name,
				icon: this.activity.icon,
				description: this.activity.description,
				location_id: this.activity.location_id,
				max_attendees: this.activity.max_attendees,
				public_event: this.activity.public_event,
				status: this.activity.status,
				state: formValue[FLEX_PERIOD_REPEATS_KEY] ? formValue[FLEX_PERIOD_REPEATS_KEY] : this.activity.state,
				flex_period_id: formValue[FLEX_PERIOD_ID_KEY] ? formValue[FLEX_PERIOD_ID_KEY] : this.activity.flex_period_id,
				schedule_type: formValue[SCHEDULE_TYPE_KEY] ? formValue[SCHEDULE_TYPE_KEY] : this.activity.schedule_type,
				repeat_end_date: formValue[SCHEDULE_REPEAT_END_DATE_KEY]
					? formValue[SCHEDULE_REPEAT_END_DATE_KEY]?.toISOString()
					: this.activity.repeat_end_date,
				visibility: this.activity.visibility,
				visibility_student_ids: this.activity.visibility_student_ids,
				visibility_group_ids: this.activity.visibility_group_ids,
			};
			if (formValue[SCHEDULE_TYPE_KEY] === 'custom' && formValue[CUSTOM_SCHEDULE_DATE_KEY]) {
				const timezone = this.httpService.currentSchoolSubject.getValue()!.timezone;
				const time_range = formValue[CUSTOM_SCHEDULE_TIME_RANGE_KEY] as TimeRangeInputType;
				const date_str = formValue[CUSTOM_SCHEDULE_DATE_KEY].format('YYYY-MM-DD');
				const start_time = moment.tz(date_str + ' ' + time_range.start, timezone);
				const end_time = moment.tz(date_str + ' ' + time_range.end, timezone);
				activityData.custom_start_time = start_time.toISOString();
				activityData.custom_end_time = end_time.toISOString();
				activityData.custom_repeat_rule = formValue[CUSTOM_SCHEDULE_REPEAT_KEY];
			}

			if (activityData.state === 'scheduled') {
				activityData.selected_instance_times = this.getSelectedInstanceTimes(activityData.state);
			}

			const toBeDeletedCountReq: ToBeDeletedCountReq = {
				activity_id: this.activity.id,
				activity_data: activityData,
			};
			this.sessionRemovalCount = 0;
			this.sessionRemovalCount = await firstValueFrom(this.schoolActivityService.GetToBeDeletedCountHTTP(toBeDeletedCountReq));
			this.sessionRemovalButton = `Delete ${this.sessionRemovalCount} Sessions`;
			if (this.sessionRemovalCount > 0) {
				this.destroyDialogRef = this.backpackDialogService.openDialog(this.warningModalBody, this.cdr);
				return;
			}
			if (scheduleType === 'flex_period') {
				this.handleFlexPeriodSave(updateReq, scheduleTypeChanged);
			} else {
				void this.handleCustomScheduleSave(updateReq, scheduleTypeChanged);
			}
			if (this.editScheduleDialogRef) {
				this.editScheduleDialogRef.close();
				this.editScheduleDialogRef = null;
			}
		} else {
			if (scheduleType === 'flex_period') {
				this.handleFlexPeriodSave(updateReq, scheduleTypeChanged);
			} else {
				void this.handleCustomScheduleSave(updateReq, scheduleTypeChanged);
			}
			if (this.editScheduleDialogRef) {
				this.editScheduleDialogRef.close();
				this.editScheduleDialogRef = null;
			}
		}
	}

	private handleFlexPeriodSave(req: Partial<UpdateSchoolActivityRequest>, scheduleTypeChanged: boolean) {
		if (!this.editScheduleForm.value[FLEX_PERIOD_ID_KEY] || !this.editScheduleForm.value[FLEX_PERIOD_REPEATS_KEY]) {
			return;
		}

		req.flex_period_id = this.editScheduleForm.value[FLEX_PERIOD_ID_KEY];
		req.state = this.editScheduleForm.value[FLEX_PERIOD_REPEATS_KEY];
		req.repeat_end_date = req.state == 'flex_recurring' ? this.editScheduleForm.value[SCHEDULE_REPEAT_END_DATE_KEY]?.toISOString() : null;

		if (scheduleTypeChanged) {
			req.custom_start_time = null;
			req.custom_end_time = null;
			req.custom_repeat_rule = '';
		}

		if (req.state === 'scheduled') {
			req.selected_instance_times = this.getSelectedInstanceTimes(req.state);
			// Determine if we're changing states or just modifying the same state
			const isChangingState = this.activity.state !== req.state;
			const instanceTimes: InstanceTimes[] = this.editScheduleForm.value[FLEX_INSTANCE_TIMES_KEY] ?? [];

			if (!isChangingState) {
				// For modifications to existing "scheduled" activities,
				// we need to include existing instances along with new selections

				// Get existing instances that weren't shown in the selection UI
				const existingInstanceTimes = this.activityInstances$.value.map((instance) => ({
					start: new Date(instance.start_time),
					end: new Date(instance.end_time),
				}));

				// Combine new selections with existing instances
				const combinedTimes = [...instanceTimes, ...existingInstanceTimes];
				req.selected_instance_times = ToWireInstanceTimes(combinedTimes);
			} else {
				// For state changes, only use the newly selected instances
				req.selected_instance_times = ToWireInstanceTimes(instanceTimes);
			}
		}

		this.updateActivity(req);
	}

	private getSelectedInstanceTimes(state: SchoolActivityState): WireInstanceTimes[] {
		if (state === 'scheduled') {
			// Determine if we're changing states or just modifying the same state
			const isChangingState = this.activity.state !== state;
			const instanceTimes: InstanceTimes[] = this.editScheduleForm.value[FLEX_INSTANCE_TIMES_KEY] ?? [];

			if (!isChangingState) {
				// For modifications to existing "scheduled" activities,
				// we need to include existing instances along with new selections

				// Get existing instances that weren't shown in the selection UI
				const existingInstanceTimes = this.activityInstances$.value.map((instance) => ({
					start: new Date(instance.start_time),
					end: new Date(instance.end_time),
				}));

				// Combine new selections with existing instances
				const combinedTimes = [...instanceTimes, ...existingInstanceTimes];
				return ToWireInstanceTimes(combinedTimes);
			} else {
				// For state changes, only use the newly selected instances
				return ToWireInstanceTimes(instanceTimes);
			}
		}
		return [];
	}

	private async handleCustomScheduleSave(req: Partial<UpdateSchoolActivityRequest>, scheduleTypeChanged: boolean): Promise<void> {
		if (!this.editScheduleForm.value[CUSTOM_SCHEDULE_DATE_KEY] || !this.editScheduleForm.value[CUSTOM_SCHEDULE_TIME_RANGE_KEY]) {
			return;
		}

		const { startTime, endTime } = getCustomStartEndTime(
			this.editScheduleForm.value[CUSTOM_SCHEDULE_DATE_KEY],
			this.editScheduleForm.value[CUSTOM_SCHEDULE_TIME_RANGE_KEY]
		);

		req.custom_start_time = startTime.toISOString();
		req.custom_end_time = endTime.toISOString();
		req.custom_repeat_rule = this.editScheduleForm.value[CUSTOM_SCHEDULE_REPEAT_KEY];
		req.repeat_end_date = req.custom_repeat_rule ? this.editScheduleForm.value[SCHEDULE_REPEAT_END_DATE_KEY]?.toISOString() : null;

		if (scheduleTypeChanged) {
			req.flex_period_id = -1;
		}

		this.updateActivity(req);
	}

	dismissWarningModal(): void {
		this.destroyDialogRef?.close();
		this.destroyDialogRef = null;
	}

	protected confirmScheduleChanges(): void {
		this.destroyDialogRef?.close();
		this.destroyDialogRef = null;
		void this.saveScheduleChanges(true);
	}
}
